import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Alert,
  CircularProgress,
  Box,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  useShareDeviceMutation,
  useGetDeviceSharesQuery,
  useRemoveDeviceShareMutation,
} from '../../state/api';

interface DeviceShareModalProps {
  open: boolean;
  onClose: () => void;
  deviceId: string;
  deviceName: string;
}

export default function DeviceShareModal({
  open,
  onClose,
  deviceId,
  deviceName,
}: DeviceShareModalProps) {
  const [email, setEmail] = useState('');
  const [accessLevel, setAccessLevel] = useState<'read' | 'full'>('read');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const {
    data: shares = [],
    isLoading: sharesLoading,
    error: sharesError,
  } = useGetDeviceSharesQuery(deviceId, {
    skip: !open,
  });

  const [shareDevice, { isLoading: shareLoading }] = useShareDeviceMutation();
  const [removeShare] = useRemoveDeviceShareMutation();

  const handleShare = async () => {
    setError('');
    setSuccess('');
    try {
      await shareDevice({ deviceId, email, accessLevel }).unwrap();
      setEmail('');
      setAccessLevel('read');
      setSuccess('Zařízení úspěšně sdíleno!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.data?.message || 'Nepodařilo se sdílet zařízení');
    }
  };

  const handleRemoveShare = async (shareId: string) => {
    try {
      await removeShare({ deviceId, shareId }).unwrap();
    } catch (err) {
      console.error('Nepodařilo se odebrat sdílení:', err);
      setError('Nepodařilo se odebrat sdílení');
    }
  };

  const handleClose = () => {
    setEmail('');
    setAccessLevel('read');
    setError('');
    setSuccess('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Sdílet zařízení: {deviceName}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Email uživatele"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            placeholder="uzivatel@example.com"
          />

          <FormControl component="fieldset" sx={{ mt: 2, width: '100%' }}>
            <FormLabel component="legend">Úroveň přístupu</FormLabel>
            <RadioGroup
              value={accessLevel}
              onChange={(e) => setAccessLevel(e.target.value as 'read' | 'full')}
            >
              <FormControlLabel
                value="read"
                control={<Radio />}
                label="Pouze čtení (grafy a data)"
              />
              <FormControlLabel
                value="full"
                control={<Radio />}
                label="Plný přístup (nastavení a plány)"
              />
            </RadioGroup>
          </FormControl>

          <Button
            variant="contained"
            onClick={handleShare}
            disabled={shareLoading || !email}
            sx={{ mt: 2 }}
            fullWidth
          >
            {shareLoading ? 'Sdílím...' : 'Sdílet zařízení'}
          </Button>
        </Box>

        <Box>
          <FormLabel component="legend" sx={{ mb: 1 }}>
            Sdíleno s:
          </FormLabel>
          {sharesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : sharesError ? (
            <Alert severity="error">Nepodařilo se načíst sdílení</Alert>
          ) : shares.length === 0 ? (
            <Alert severity="info">Zařízení není sdíleno s žádným uživatelem</Alert>
          ) : (
            <List>
              {shares.map((share: any) => (
                <ListItem
                  key={share._id}
                  secondaryAction={
                    <IconButton edge="end" onClick={() => handleRemoveShare(share._id)}>
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={share.userId?.email || 'Neznámý uživatel'}
                    secondary={
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                        <Chip
                          label={share.accessLevel === 'read' ? 'Pouze čtení' : 'Plný přístup'}
                          size="small"
                          color={share.accessLevel === 'read' ? 'default' : 'primary'}
                        />
                        <span style={{ fontSize: '0.875rem', color: '#666' }}>
                          Sdíleno {new Date(share.sharedAt).toLocaleDateString('cs-CZ')}
                        </span>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Zavřít</Button>
      </DialogActions>
    </Dialog>
  );
}
